import React, { useState, useEffect, useCallback, Fragment } from 'react';
import SessionForm from '../components/SessionForm';
import axios from 'axios';
import SessionList from '../components/SessionList';
import ImportSessionsModal from '../components/ImportSessionsModal';
import { Dialog, Transition } from '@headlessui/react';
import {
    PlusIcon,
    XMarkIcon,
    ArrowUpTrayIcon
} from '@heroicons/react/24/outline';
import { clearCacheForEndpoint } from '../utils/axiosConfig';

// Reusable Select Component
const Select = ({ label, value, onChange, options, name }) => (
    <div className="relative">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            {label}
        </label>
        <select
            name={name}
            value={value}
            onChange={onChange}
            className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 
                     bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white 
                     focus:border-primary-500 dark:focus:border-primary-400 
                     focus:outline-none focus:ring-1 focus:ring-primary-500 dark:focus:ring-primary-400"
        >
            <option value="">All</option>
            {options.map(option => {
                // For locations and stakes, use id as value
                const value = option.id !== undefined ? option.id : option.value;
                const label = option.name || option.label;
                return (
                    <option key={value} value={value}>
                        {label}
                    </option>
                );
            })}
        </select>
    </div>
);

function SessionsPage() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [filters, setFilters] = useState({ timeframe: 'ALL_TIME', location_id: '', stake_id: '', reviewed: '' });
    const [locations, setLocations] = useState([]);
    const [stakes, setStakes] = useState([]);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showResetDialog, setShowResetDialog] = useState(false);


    const fetchSessions = useCallback((forceRefresh = false) => {
        // PERFORMANCE: Clear cache if force refresh is requested
        if (forceRefresh) {
            clearCacheForEndpoint('/api/sessions/');
        }

        setLoading(true);
        // Remove empty filter values
        const activeFilters = Object.entries(filters).reduce((acc, [key, value]) => {
            if (value) {
                acc[key] = value;
            }
            return acc;
        }, {});

        axios.get('/api/sessions/', {
            params: {
                ...activeFilters,
                ordering: '-start_time'  // Order by date descending (newest first)
            }
        })
        .then(res => {
            setSessions(res.data);
            setLoading(false);
        })
        .catch(err => {
            setError('Failed to fetch sessions. ' + (err.response?.data?.detail || err.message));
            setLoading(false);
        });
    }, [filters]);

    useEffect(() => {
        axios.get('/api/locations/')
            .then(res => setLocations(res.data || []))
            .catch(err => console.error('Failed to fetch locations:', err));
        axios.get('/api/stakes/')
            .then(res => setStakes(res.data || []))
            .catch(err => console.error('Failed to fetch stakes:', err));
    }, []);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);  // Remove refreshTrigger from dependencies

    const handleFilterChange = (e) => {
        setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
    };

    const handleResetSessions = async () => {
        try {
            await axios.post('/api/sessions/reset/');
            await fetchSessions();  // Make sure to await the fetch
            setShowResetDialog(false);
            // Force refresh of other components that might show session data
            if (window.location.pathname === '/sessions') {
                window.location.reload();  // Force a full page refresh
            }
        } catch (err) {
            alert(err.response?.data?.error || 'Failed to reset sessions.');
        }
    };

    if (error) {
        return (
            <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 
                          text-red-700 dark:text-red-400 m-4">
                {error}
            </div>
        );
    }

    const dateOptions = [
        { value: 'ALL_TIME', label: 'All Time' },
        { value: 'LAST_7_DAYS', label: 'Last 7 Days' },
        { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
        { value: 'LAST_3_MONTHS', label: 'Last 3 Months' },
        { value: 'LAST_6_MONTHS', label: 'Last 6 Months' },
        { value: 'LAST_12_MONTHS', label: 'Last 12 Months' },
        { value: 'THIS_YEAR', label: 'This Year' },
        { value: 'LAST_YEAR', label: 'Last Year' },
        { value: 'THIS_MONTH', label: 'This Month' },
        { value: 'LAST_MONTH', label: 'Last Month' },
        { value: 'THIS_WEEK', label: 'This Week' },
        { value: 'LAST_WEEK', label: 'Last Week' },
    ];

    return (
        <div className="max-w-7xl mx-auto mt-8 mb-8">
            <div className="bg-white dark:bg-gray-800 border border-gray-200 
                          dark:border-gray-700 rounded-2xl p-4 sm:p-6 mb-6 
                          shadow-sm relative">
                <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                        Sessions
                    </h1>
                    <div className="flex items-center gap-2">


                        {/* Import Button */}
                        <button
                            onClick={() => setShowImportModal(true)}
                            className="p-2 sm:p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 
                                     shadow-sm hover:shadow transition-all duration-200 
                                     transform hover:scale-105 focus:outline-none focus:ring-2 
                                     focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            title="Import Sessions CSV"
                        >
                            <ArrowUpTrayIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>

                        {/* Add Button */}
                        <button
                            onClick={() => setShowAdd(true)}
                            className="p-2 sm:p-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 
                                     shadow-sm hover:shadow transition-all duration-200 
                                     transform hover:scale-105 focus:outline-none focus:ring-2 
                                     focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                            title="Add Session"
                        >
                            <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>
                    </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
                    <Select
                        label="Date"
                        name="timeframe"
                        value={filters.timeframe}
                        onChange={handleFilterChange}
                        options={dateOptions}
                    />
                    <Select
                        label="Location"
                        name="location_id"
                        value={filters.location_id}
                        onChange={handleFilterChange}
                        options={locations}
                    />
                    <Select
                        label="Stake"
                        name="stake_id"
                        value={filters.stake_id}
                        onChange={handleFilterChange}
                        options={stakes}
                    />
                    <Select
                        label="Review Status"
                        name="reviewed"
                        value={filters.reviewed}
                        onChange={handleFilterChange}
                        options={[
                            { value: 'false', label: 'Needs Review' },
                            { value: 'true', label: 'Reviewed' }
                        ]}
                    />
                </div>

                {/* Session List */}
                <div className="mt-4">
                    <SessionList
                        cardHeight="auto"
                        sessions={sessions}
                        loading={loading}
                        error={error}
                        noPaper
                        hideAddButton
                        onUpdate={fetchSessions}
                    />
                </div>
            </div>

            {/* Add Session Modal */}
            <Transition appear show={showAdd} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setShowAdd(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden 
                                                       rounded-2xl bg-white dark:bg-gray-800 
                                                       border border-gray-200 dark:border-gray-700
                                                       p-6 text-left align-middle shadow-xl transition-all">
                                    <div className="flex justify-between items-center mb-4 
                                                  border-b border-gray-200 dark:border-gray-700 pb-4">
                                        <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
                                            Add Session
                                        </Dialog.Title>
                                        <button
                                            onClick={() => setShowAdd(false)}
                                            className="text-gray-400 hover:text-gray-500 
                                                     dark:hover:text-gray-300"
                                        >
                                            <XMarkIcon className="h-6 w-6" />
                                        </button>
                                    </div>
                                    <SessionForm onSuccess={() => {
                                        setShowAdd(false);
                                        fetchSessions();
                                    }} />
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

            {/* Import Modal */}
            <ImportSessionsModal
                open={showImportModal}
                onClose={() => setShowImportModal(false)}
                onSuccess={() => {
                    setShowImportModal(false);
                    fetchSessions();
                }}
            />

            {/* Reset Confirmation Dialog */}
            <Transition appear show={showResetDialog} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setShowResetDialog(false)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-md transform overflow-hidden 
                                                       rounded-2xl bg-white dark:bg-gray-800 
                                                       border border-gray-200 dark:border-gray-700
                                                       p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white 
                                                           border-b border-gray-200 dark:border-gray-700 pb-4">
                                        Reset All Sessions
                                    </Dialog.Title>

                                    <div className="mt-4">
                                        <p className="text-gray-600 dark:text-gray-400">
                                            Are you sure you want to delete <strong>all</strong> sessions? 
                                            This action cannot be undone.
                                        </p>
                                    </div>

                                    <div className="mt-6 flex justify-end gap-3 pt-4 
                                                  border-t border-gray-200 dark:border-gray-700">
                                        <button
                                            onClick={() => setShowResetDialog(false)}
                                            className="px-4 py-2 text-sm font-medium text-gray-700 
                                                     dark:text-gray-300 hover:text-gray-900 
                                                     dark:hover:text-white"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleResetSessions}
                                            className="px-4 py-2 text-sm font-medium text-white 
                                                     bg-red-600 rounded-lg hover:bg-red-700 
                                                     focus:outline-none focus:ring-2 
                                                     focus:ring-red-500 focus:ring-offset-2"
                                        >
                                            Delete All
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </div>
    );
}

export default SessionsPage; 